/**
 * CollapsibleSection Component
 * A simple collapsible section with header and content.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CollapsibleSectionProps {
  /** Section title */
  title: string;
  /** Optional icon to show before title */
  icon?: React.ReactNode;
  /** Whether section is collapsed by default */
  defaultCollapsed?: boolean;
  /** Optional badge/indicator to show in header */
  badge?: React.ReactNode;
  /** Section content */
  children: React.ReactNode;
  /** Additional className for the container */
  className?: string;
  /**
   * Set by a caller whose form can lock: the content is then a fieldset, disabled while this is
   * true, and the header still opens and closes the section so locked fields can be read.
   * Unset, the content is a plain container.
   */
  contentDisabled?: boolean;
  /** Prefix of the data-testid of the header button (`-toggle`) and of the content (`-content`). */
  testId?: string;
}

/**
 * CollapsibleSection provides a collapsible container with a clickable header.
 */
export function CollapsibleSection({
  title,
  icon,
  defaultCollapsed = false,
  badge,
  children,
  className,
  contentDisabled,
  testId,
}: CollapsibleSectionProps): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <div className={cn('rounded-lg border', className)}>
      <button
        type="button"
        onClick={() => { setIsCollapsed(!isCollapsed); }}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        aria-expanded={!isCollapsed}
        data-testid={testId === undefined ? undefined : `${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium">{title}</span>
          {badge}
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            isCollapsed && '-rotate-90'
          )}
        />
      </button>
      {!isCollapsed && contentDisabled === undefined && (
        <div className="border-t px-4 pb-4 pt-3" data-testid={testId === undefined ? undefined : `${testId}-content`}>
          {children}
        </div>
      )}
      {!isCollapsed && contentDisabled !== undefined && (
        <fieldset
          disabled={contentDisabled}
          className={cn('min-w-0 border-t px-4 pb-4 pt-3', contentDisabled && 'opacity-60')}
          data-testid={testId === undefined ? undefined : `${testId}-content`}
        >
          {children}
        </fieldset>
      )}
    </div>
  );
}
